import React, { useEffect } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Typography,
  Button,
  FormHelperText,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";

export interface FilterLogsFormData {
  search: string;
  level: string;
  dateFrom: string;
  dateTo: string;
}

interface FilterLogsProps {
  searchFilters?: FilterLogsFormData | null;
  onCancel?: () => void;
  onFilter?: (data: FilterLogsFormData) => void;
}

const FilterLogs = ({ searchFilters, onCancel, onFilter }: FilterLogsProps) => {
  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<FilterLogsFormData>({
    defaultValues: {
      search: "",
      level: "",
      dateFrom: "",
      dateTo: "",
    },
  });

  const dateFrom = watch("dateFrom");
  const dateTo = watch("dateTo");

  useEffect(() => {
    if (searchFilters) {
      reset({
        search: searchFilters.search || "",
        level: searchFilters.level || "",
        dateFrom: searchFilters.dateFrom || "",
        dateTo: searchFilters.dateTo || "",
      });
    }
  }, [searchFilters]);

  const onSubmitHandler = (data: FilterLogsFormData) => {
    if (onFilter) {
      onFilter(data);
    }
  };

  const handleCancel = () => {
    reset();
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmitHandler)}>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        {/* Search Input - Mensaje */}
        <Box sx={{ flex: 1, minWidth: "200px" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Mensaje
          </Typography>
          <Controller
            control={control}
            name="search"
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="Buscar mensaje..."
                size="small"
                fullWidth
              />
            )}
          />
        </Box>

        {/* Level Dropdown - Nivel */}
        <Box sx={{ flex: 0.8, minWidth: "180px" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Nivel
          </Typography>
          <Controller
            control={control}
            name="level"
            render={({ field }) => (
              <FormControl fullWidth size="small">
                <Select {...field} displayEmpty>
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="Error">Error</MenuItem>
                  <MenuItem value="Information">Información</MenuItem>
                  <MenuItem value="Warning">Warning</MenuItem>
                </Select>
              </FormControl>
            )}
          />
        </Box>

        {/* Date Picker - Fecha Desde */}
        <Box sx={{ flex: 0.8, minWidth: "180px" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Fecha Desde
          </Typography>
          <Controller
            control={control}
            name="dateFrom"
            rules={{
              validate: {
                required: (value) => {
                  if (dateTo && !value) {
                    return "Fecha Desde es requerida si Fecha Hasta está llena";
                  }
                  return true;
                },
                lessThanDateTo: (value) => {
                  if (value && dateTo && value > dateTo) {
                    return "Fecha Desde debe ser menor que Fecha Hasta";
                  }
                  return true;
                },
              },
            }}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.dateFrom}>
                <TextField
                  {...field}
                  type="date"
                  size="small"
                  fullWidth
                  error={!!errors.dateFrom}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                {errors.dateFrom && (
                  <FormHelperText>{errors.dateFrom.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Box>

        {/* Date Picker - Fecha Hasta */}
        <Box sx={{ flex: 0.8, minWidth: "180px" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Fecha Hasta
          </Typography>
          <Controller
            control={control}
            name="dateTo"
            rules={{
              validate: {
                required: (value) => {
                  if (dateFrom && !value) {
                    return "Fecha Hasta es requerida si Fecha Desde está llena";
                  }
                  return true;
                },
                greaterThanDateFrom: (value) => {
                  if (value && dateFrom && value < dateFrom) {
                    return "Fecha Hasta debe ser mayor que Fecha Desde";
                  }
                  return true;
                },
              },
            }}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.dateTo}>
                <TextField
                  {...field}
                  type="date"
                  size="small"
                  fullWidth
                  error={!!errors.dateTo}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                {errors.dateTo && (
                  <FormHelperText>{errors.dateTo.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
        <Button variant="outlined" color="primary" onClick={handleCancel}>
          Cancelar
        </Button>
        <Button type="submit" variant="contained" color="primary">
          Filtrar
        </Button>
      </Box>
    </form>
  );
};

export default FilterLogs;
